import { colors } from '@lmc/common/constants'
import type { Carer } from '@lmc/ts-common'
import _ from 'lodash'
import React, { Component, type CSSProperties, type ReactNode } from 'react'
import { connect } from 'react-redux'
import { mixpanel } from '../../../common/services/analytics'
import { ScrollLock } from '../../../LEGACY/elemental'
import Operations from '../../../operations'
import { Step, StepList } from '../../generic'
import Modal from '..'
import {
  FamilyPortalComplete,
  FamilyPortalFeatures,
  FamilyPortalItems,
  FamilyPortalPayment
} from './components'

const MODAL_ID = 'setupFamilyPortal'

interface FamilyPortalSetupModalProps {
  close: () => void
  isOpen: boolean
  carers: Carer[]
  categories: any[]
  homeName: string
  items: any[]
  preferredCarePlansNominative: string
  residents: any[]
  preferredPeopleNominative: string
  home: any
  setFormField: (args: { key: string; value: any }) => void
  updateItem: (args: any) => void
  showToast: (args: { title: string }) => void
  user: any // Define the user type
  refreshHomeDetails: (homeId: string) => void
}

export class FamilyPortalSetupModal extends Component<FamilyPortalSetupModalProps> {
  handleSubmit = async (next: () => void) => {
    const { showToast, setFormField, updateItem, user, refreshHomeDetails } =
      this.props

    // Submit the form after selecting a payment method on the third step
    // https://logmycare.atlassian.net/browse/CARE-1254
    // Set the home's family portal preferences

    // The form data is stored in Redux:
    // stats.forms.setupFamilyPortal.fields:
    //  familyCarePlans: true / false
    //  familyDocuments: true / false
    //  familyLogCategoryItems: [ list of item IDs ]
    //  familyMedications: true / false
    //  familyShowDates: true / false
    //  familyShowNames: true / false
    //  familyPaymentType: 'home' || 'family'
    setFormField({ key: 'familySetupComplete', value: true })

    updateItem({
      url: `list/homes/${user.home}`,
      onComplete: () => {
        showToast({ title: 'Setup Complete' })
        // refresh the home settings state so that the preferences form is up to date
        refreshHomeDetails(user.home)
        next()
      },
      keepFormOpen: true // prevents the modal from closing
    })
  }

  renderSteps(): ReactNode {
    const { close, carers, homeName, items, home, ...formProps } = this.props

    const stepStyles = { padding: '0px 55px 30px' }

    return (
      <StepList onClose={close} closeLabel="Cancel">
        <Step
          formId={MODAL_ID}
          component={FamilyPortalFeatures}
          {...formProps}
          {...{ home }}
        />
        <Step
          formId={MODAL_ID}
          component={FamilyPortalItems}
          stepStyles={stepStyles}
          {...formProps}
        />
        <Step
          formId={MODAL_ID}
          component={FamilyPortalPayment}
          data={{ residents: formProps.residents, homeName }}
          stepStyles={stepStyles}
          onComplete={this.handleSubmit}
          {...formProps}
        />
        <Step
          formId={MODAL_ID}
          component={FamilyPortalComplete}
          data={{ residents: formProps.residents, carers }}
          close={close}
          preferredTerm={formProps.preferredPeopleNominative}
          stepStyles={stepStyles}
          {...formProps}
        />
      </StepList>
    )
  }

  render() {
    const { isOpen } = this.props

    if (!isOpen) return null

    return (
      <div style={styles.container as CSSProperties}>
        {this.renderSteps()}
        <ScrollLock />
      </div>
    )
  }
}

const styles = {
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    margin: '0 auto',
    zIndex: 100,
    background: colors.white
  }
}

const mapStateToProps = (state: any) => ({
  carers: state.data.carers,
  categories: state.data['log-categories'],
  homeName: state.global.home.name,
  items: state.data['log-category-items'],
  preferredCarePlansNominative: state.global.home.preferredCarePlansNominative,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  residents: _.filter(state.data.residents, { status: 'active' }),
  home: state.global.home
})

const mapDispatchToProps = (dispatch: any) => ({
  refreshHomeDetails: (homeId: string) => {
    dispatch(Operations.getHomeDetails(homeId))
  },
  mixpanel
})

const wrappedModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(FamilyPortalSetupModal)
export default Modal(wrappedModal, MODAL_ID)
